import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import type { PortalAccount, PortalSubscription } from '@api/customer-portal-api';
import { usePaymentMethod } from '@hooks/portal/useSubscriptions';
import type { PortalPerks } from '@lib/customerPortalContent';
import { capturePortalEvent } from '@lib/monitoring';
import { formatDiscountDuration, nextChargeDiscount } from './discountOffer';
import PauseSubscriptionDialog from './PauseSubscriptionDialog';
import {
  fmtAmount,
  fmtUtcDate,
  fmtSubscriptionFrequency,
  formatAddressLines,
} from './portalFormat';
import { rewardBannerOf } from './rewardBanner';
import RewardBannerImage from './RewardBannerImage';
import SubStatusBadge from './SubStatusBadge';
import {
  canCancelSubscription,
  canPauseSubscription,
  lastChargedDate,
  nextOrderState,
  paymentMethodLines,
  subscriptionItems,
} from './subscriptionDetails';
import {
  getSubscriptionDisplayStatus,
  isEndedSubscriptionDisplayStatus,
} from './subscriptionStatus';

export interface SubscriptionDetailsPageProps {
  subscription: PortalSubscription;
  account: PortalAccount | null; // shipping address source
  perks: PortalPerks | null;
  txt1: string; // theme text colours from the shell
  txt2: string;
  portalContainer: Element | null;
  onBack: () => void;
  onCancel: (sub: PortalSubscription) => void; // the shell's existing openCancel
}

const CARD_STYLE: React.CSSProperties = {
  border: '1px solid #E5E5E5',
  backgroundColor: '#FFFFFF',
  boxShadow: '0px 1px 3px 0px #0000001A',
};

const InfoCard: React.FC<{
  title: string;
  txt1: string;
  txt2: string;
  children: React.ReactNode;
}> = ({ title, txt1, txt2, children }) => (
  <div className="rounded-[10px] p-4" style={CARD_STYLE}>
    <div className="text-[12px] mb-1" style={{ color: txt2 }}>
      {title}
    </div>
    <div className="text-[14px] primary-font-medium" style={{ color: txt1 }}>
      {children}
    </div>
  </div>
);

const PaymentCard: React.FC<{ subscriptionId: string; txt1: string; txt2: string }> = ({
  subscriptionId,
  txt1,
  txt2,
}) => {
  const { t } = useTranslation();
  const query = usePaymentMethod(subscriptionId);
  // A failed lookup (Whop down, no card on file) hides the card; the rest of the page stays.
  if (query.isError) return null;
  const lines = query.isPending ? null : paymentMethodLines(t, query.data);
  if (!query.isPending && !lines) return null;
  return (
    <InfoCard title={t('subscriptionDetails.payment.title')} txt1={txt1} txt2={txt2}>
      {lines ? (
        <>
          <div>{lines.card}</div>
          {lines.expires && (
            <div className="text-[13px] font-normal" style={{ color: txt2 }}>
              {lines.expires}
            </div>
          )}
        </>
      ) : (
        <div
          data-testid="payment-loading"
          className="h-4 w-32 rounded animate-pulse"
          style={{ backgroundColor: '#E5E5E5' }}
        />
      )}
    </InfoCard>
  );
};

const SubscriptionDetailsPage: React.FC<SubscriptionDetailsPageProps> = ({
  subscription: sub,
  account,
  perks,
  txt1,
  txt2,
  portalContainer,
  onBack,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [pauseOpen, setPauseOpen] = useState(false);

  // Once per subscription shown, not per status change.
  useEffect(() => {
    capturePortalEvent('portal_subscription_details_viewed', {
      subscription_id: sub.id,
      subscription_status: sub.status,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub.id]);

  const rawStatus = getSubscriptionDisplayStatus(sub.status, false);
  const displayStatus = getSubscriptionDisplayStatus(sub.status, sub.cancel_at_period_end);
  const isEnded = isEndedSubscriptionDisplayStatus(rawStatus);
  const isCancelPending = sub.cancel_at_period_end && !isEnded && rawStatus !== 'paused';
  // Same price rules as the main-page card: a retention discount only on live subscriptions.
  const retention = isEnded || isCancelPending ? null : nextChargeDiscount(sub);
  const priceCents = retention?.discountedCents ?? sub.amount;
  const strikeCents = retention?.fullCents ?? sub.original_amount;

  const nextOrder = nextOrderState(sub);
  const lastCharged = lastChargedDate(sub);
  const items = subscriptionItems(sub);
  const addressLines = account?.shipping_address
    ? formatAddressLines(account.shipping_address)
    : [];
  const frequency = fmtSubscriptionFrequency(sub);

  const nextOrderText = (() => {
    switch (nextOrder.kind) {
      case 'next':
        return t('subscriptionDetails.nextOrder', { date: fmtUtcDate(nextOrder.date) });
      case 'cancels':
        return t('subscriptionDetails.cancelsOn', { date: fmtUtcDate(nextOrder.date) });
      case 'paused':
        return t('subscriptionDetails.pausedUntil', { date: fmtUtcDate(nextOrder.resumesAt) });
      case 'pausedIndefinitely':
        return t('dashboard.card.resumesWhenReactivated');
      case 'ended': {
        if (!nextOrder.date) {
          return nextOrder.expired ? t('dashboard.card.expired') : t('dashboard.card.ended');
        }
        const key = nextOrder.expired ? 'expiredOn' : 'endedOn';
        return t(`subscriptionDetails.${key}`, { date: fmtUtcDate(nextOrder.date) });
      }
    }
  })();

  return (
    <div>
      <div className="mb-6">
        <button
          type="button"
          onClick={onBack}
          className="text-[14px] font-medium px-4 py-2 rounded-[6px] border transition-opacity hover:opacity-80"
          style={{ borderColor: '#E5E5E5', color: txt1, backgroundColor: '#FFFFFF' }}
        >
          {`‹ ${t('subscriptionDetails.back')}`}
        </button>
      </div>

      <div className="rounded-[10px] p-5 mb-6" style={CARD_STYLE}>
        <div className="flex justify-between items-center gap-2 mb-3">
          <span className="primary-font-semibold text-[18px]" style={{ color: txt1 }}>
            {`# ${sub.subscription_display_id}`}
          </span>
          <SubStatusBadge status={displayStatus} />
        </div>
        <div className="flex items-baseline gap-2 flex-wrap mb-2">
          <span className="primary-font-medium text-[18px]" style={{ color: txt1 }}>
            {sub.shipping
              ? t('subscriptionDetails.shippingPerDelivery', {
                  price: fmtAmount(priceCents, sub.currency),
                  shipping: fmtAmount(sub.shipping, sub.currency),
                })
              : t('subscriptionDetails.pricePerDelivery', {
                  price: fmtAmount(priceCents, sub.currency),
                })}
          </span>
          {strikeCents > priceCents && (
            <span className="text-[12px] line-through" style={{ color: txt2 }}>
              {fmtAmount(strikeCents, sub.currency)}
            </span>
          )}
        </div>
        {retention && (
          <div className="mb-2">
            <span
              className="inline-flex text-[12px] px-2 py-0.5 rounded-full border primary-font-medium"
              style={{ borderColor: '#BBF7D0', backgroundColor: '#F0FDF4', color: '#15803D' }}
            >
              {t('retention.discount.summary', {
                percent: retention.terms.percentage,
                duration: formatDiscountDuration(t, retention.terms),
              })}
            </span>
          </div>
        )}
        <div className="flex items-center gap-1.5 text-[13px]" style={{ color: txt2 }}>
          <RefreshCw size={13} />
          <span>{frequency}</span>
        </div>
      </div>

      <RewardBannerImage banner={rewardBannerOf([sub])} />

      <div className="rounded-[10px] p-5 mb-6" style={CARD_STYLE}>
        <div className="text-[15px] primary-font-medium" style={{ color: txt1 }}>
          {nextOrderText}
        </div>
        {nextOrder.kind === 'paused' && nextOrder.nextOrder && (
          <div className="text-[13px] mt-1" style={{ color: txt2 }}>
            {t('subscriptionDetails.nextOrderPlacedOn', { date: fmtUtcDate(nextOrder.nextOrder) })}
          </div>
        )}
        {lastCharged && (
          <div className="text-[13px] mt-2" style={{ color: txt2 }}>
            {t('subscriptionDetails.lastCharged', { date: fmtUtcDate(lastCharged) })}
          </div>
        )}
      </div>

      <h2 className="primary-font-semibold text-[18px] mb-3" style={{ color: txt1 }}>
        {t('subscriptionDetails.sectionTitle')}
      </h2>

      {items.length > 0 && (
        <div className="flex flex-col gap-3 mb-4">
          {items.map((li, i) => {
            const unitCents = li.subscription?.recurring_price ?? li.price;
            return (
              <div
                // Consolidated duplicates can repeat a cart item id.
                key={`${li.id}-${i}`}
                className="rounded-[10px] p-4 flex items-start gap-3"
                style={CARD_STYLE}
              >
                {li.product_image && (
                  <img
                    src={li.product_image}
                    alt={li.name}
                    className="w-14 h-14 rounded-[8px] object-cover flex-shrink-0"
                    style={{ border: '1px solid #E5E5E5', backgroundColor: '#FFFFFF' }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] primary-font-medium" style={{ color: txt1 }}>
                    {li.name}
                  </div>
                  {li.selected_options.length > 0 && (
                    <div
                      className="flex flex-wrap items-center gap-2 text-[12px] mt-1"
                      style={{ color: txt2 }}
                    >
                      {li.selected_options.map(({ name, value }, i) => (
                        <span
                          key={`${name}-${i}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border rounded"
                          style={{ borderColor: '#E5E5E5', color: txt1 }}
                        >
                          <span style={{ color: txt2 }}>{name}:</span>
                          <span className="primary-font-medium">{value}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="text-[12px] mt-1" style={{ color: txt2 }}>
                    {t('subscriptionDetails.qty', { count: li.quantity })}
                  </div>
                </div>
                <div
                  className="text-[14px] primary-font-medium flex-shrink-0"
                  style={{ color: txt1 }}
                >
                  {fmtAmount(unitCents * li.quantity, li.currency)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <InfoCard title={t('subscriptionDetails.plan')} txt1={txt1} txt2={txt2}>
          {frequency}
        </InfoCard>
        {addressLines.length > 0 && (
          <InfoCard title={t('subscriptionDetails.shippingAddress')} txt1={txt1} txt2={txt2}>
            {addressLines.map((line, i) => (
              <div key={`${line}-${i}`}>{line}</div>
            ))}
          </InfoCard>
        )}
        <PaymentCard subscriptionId={sub.id} txt1={txt1} txt2={txt2} />
      </div>

      {canPauseSubscription(sub) && (
        <div className="text-center mb-6">
          <button
            type="button"
            onClick={() => setPauseOpen(true)}
            className="inline-block mx-auto text-[14px] font-medium px-6 py-2 rounded-[6px] border transition-opacity hover:opacity-80"
            style={{ borderColor: '#111827', color: '#111827', backgroundColor: '#FFFFFF' }}
          >
            {t('subscriptionDetails.pauseButton')}
          </button>
        </div>
      )}

      {/* Outside the gate: the pause refetches the list, and once the subscription stops being
          pausable the dialog must stay to show the outcome until it closes itself. */}
      <PauseSubscriptionDialog
        subscription={sub}
        open={pauseOpen}
        onClose={() => setPauseOpen(false)}
        portalContainer={portalContainer}
      />

      {perks && (
        <div className="border-t pt-5 mb-6" style={{ borderColor: '#E5E5E5' }}>
          <h3 className="primary-font-semibold text-[16px] mb-2" style={{ color: txt1 }}>
            {perks.heading ?? t('subscriptionDetails.perksDefaultHeading')}
          </h3>
          {perks.lines.map((line, i) => (
            <p key={`${i}-${line}`} className="text-[14px] mb-1" style={{ color: txt2 }}>
              {line}
            </p>
          ))}
        </div>
      )}

      {canCancelSubscription(sub) && (
        <div className="mb-8 text-center">
          <button
            type="button"
            onClick={() => onCancel(sub)}
            className="text-[14px] font-medium underline underline-offset-2 transition-opacity hover:opacity-80"
            style={{ color: txt1 }}
          >
            {t('subscriptionDetails.cancel')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDetailsPage;
